import * as microsoftTeams from '@microsoft/teams-js';

export interface TeamsContext {
  userId: string;
  displayName: string;
  userPrincipalName: string;
  tenantId: string;
  locale: string;
  theme: 'default' | 'dark' | 'contrast';
  hostName: string;
}

let initialized = false;
let inTeams = false;

async function initialize(): Promise<void> {
  if (initialized) return;
  await microsoftTeams.app.initialize();
  initialized = true;
}

/**
 * 2026-09-04 — cuánto se espera al handshake real de `microsoftTeams.app.initialize()` antes de
 * decidir "no estamos en Teams". Antes eran 2000ms, repetidos e independientes en `usePlatform.ts`
 * y `AuthProvider.tsx`: demasiado corto para un tab de REUNIÓN (resuelve contexto adicional frente
 * a uno personal) y, como `Promise.race` no cancela al perdedor, perder la carrera no detenía el
 * handshake real — solo hacía que la app decidiera (mal) "no estoy en Teams", sin llamar nunca
 * `notifyAppLoaded`/`notifySuccess`. Sin esa señal, Teams entiende que el tab nunca terminó de
 * cargar y recarga el iframe — el parpadeo infinito reportado en el video de QA. Ver
 * docs/contexto/errores-conocidos.md para el diagnóstico completo.
 */
const TEAMS_INIT_TIMEOUT_MS = 15000;

/** Handshake en vuelo compartido — ver doc-comment de `detectTeams`. */
let detectPromise: Promise<TeamsContext | null> | null = null;

/**
 * Punto ÚNICO de detección de Teams: antes `usePlatform.ts` y `AuthProvider.tsx` armaban, cada uno
 * por su cuenta, su propia carrera de 2s contra `initialize()`/`getContext()` — sin coordinarse
 * entre sí, compitiendo por el mismo estado módulo-nivel (`initialized`/`inTeams`) y pudiendo
 * disparar DOS handshakes reales concurrentes. `detectTeams()` cachea la promesa en vuelo: quien
 * la llame mientras ya hay una corriendo recibe la MISMA, nunca dispara una segunda.
 *
 * Fuera de un iframe (`window === window.top`) resuelve `null` de inmediato, sin esperar nada —
 * es el caso normal de navegador, y es el 100% de las veces que NO corremos dentro de Teams.
 */
function detectTeams(): Promise<TeamsContext | null> {
  if (window === window.top) return Promise.resolve(null);
  if (!detectPromise) detectPromise = doDetectTeams();
  return detectPromise;
}

async function doDetectTeams(): Promise<TeamsContext | null> {
  try {
    await Promise.race([
      initialize(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Teams SDK timeout')), TEAMS_INIT_TIMEOUT_MS)),
    ]);
    // Se llega aquí solo si el handshake real terminó DENTRO del plazo — recién ahí Teams recibe
    // la señal de que el tab ya cargó. Si el timeout ganó la carrera, esta línea nunca se alcanza,
    // igual que antes — pero ahora con 15s de margen en vez de 2.
    notifyAppLoaded();
    const ctx = await getContext();
    if (!ctx) return null;
    markInTeams();
    notifySuccess();
    return ctx;
  } catch {
    return null;
  }
}

/** usePlatform marca esto cuando confirma que corremos dentro de Teams. */
function markInTeams(): void {
  inTeams = true;
}

function isInTeams(): boolean {
  return inTeams;
}

/**
 * Token SSO de Teams (silencioso, sin redirect). aud = a2d1655c (la app del webApplicationInfo),
 * así que las APIs (que validan aud=a2d1655c) lo aceptan directo — sin OBO.
 * Requiere que a2d1655c pre-autorice los client IDs de Teams (paso de Sigma).
 */
async function getSsoToken(): Promise<string> {
  await initialize();
  return await microsoftTeams.authentication.getAuthToken();
}

async function getContext(): Promise<TeamsContext | null> {
  try {
    const ctx = await microsoftTeams.app.getContext();
    // Teams NO siempre trae displayName (a diferencia del navegador con MSAL). Si falta,
    // lo sacamos del claim 'name' del token SSO -> el mismo nombre completo de AAD que
    // muestra el web. Sin esto el saludo cae al correo.
    let displayName = ctx.user?.displayName ?? '';
    if (!displayName) displayName = await getSsoDisplayName();
    return {
      userId: ctx.user?.id ?? '',
      displayName,
      userPrincipalName: ctx.user?.userPrincipalName ?? '',
      tenantId: ctx.user?.tenant?.id ?? '',
      locale: ctx.app?.locale ?? 'es',
      theme: (ctx.app?.theme as TeamsContext['theme']) ?? 'default',
      hostName: ctx.app?.host?.name ?? 'web',
    };
  } catch {
    return null;
  }
}

/** Decodifica el claim 'name' (nombre completo AAD) del token SSO de Teams. Vacío si algo falla. */
async function getSsoDisplayName(): Promise<string> {
  try {
    const token = await microsoftTeams.authentication.getAuthToken();
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    // Decodifica UTF-8 correctamente (acentos: "Chávez").
    const json = decodeURIComponent(
      atob(b64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    const payload = JSON.parse(json) as { name?: string };
    return payload.name ?? '';
  } catch {
    return '';
  }
}

function notifyAppLoaded(): void {
  try { microsoftTeams.app.notifyAppLoaded(); } catch { /* not in Teams */ }
}

function notifySuccess(): void {
  try { microsoftTeams.app.notifySuccess(); } catch { /* not in Teams */ }
}

function registerOnThemeChangeHandler(handler: (theme: string) => void): void {
  try {
    microsoftTeams.app.registerOnThemeChangeHandler(handler);
  } catch { /* not in Teams */ }
}

const TeamsSDK = { initialize, getContext, notifyAppLoaded, notifySuccess, registerOnThemeChangeHandler, markInTeams, isInTeams, getSsoToken, detectTeams };
export default TeamsSDK;
